import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { initNativeShell } from "@/lib/nativeBridge";

/* -- EAGER (always in the initial bundle) --
   The home/landing + the routes a signed-in user navigates between
   dozens of times per session. Putting them in the initial bundle
   eliminates the Suspense gap on every tab switch, which on a slow
   mobile network was making transitions feel choppy. */
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import UserRoute from "@/components/UserRoute";
import ProductTour from "@/components/ProductTour";
import ErrorBoundary from "@/components/ErrorBoundary";
import Landing from "@/pages/Landing";
import AuthHub from "@/pages/AuthHub";
import CaseDashboard from "@/pages/CaseDashboard";
import MessagesList from "@/pages/MessagesList";
import MessageRoom from "@/pages/MessageRoom";
import Account from "@/pages/Account";
import AccountPresence from "@/pages/AccountPresence";
import NotificationsPage from "@/pages/NotificationsPage";
import LawyersDirectory from "@/pages/LawyersDirectory";
import LawyerProfile from "@/pages/LawyerProfile";
import { SplashGate } from "@/components/SplashIntro";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import { UserAuthProvider } from "@/context/UserAuthContext";
import { AIConsentProvider } from "@/context/AIConsentContext";
import { UnreadProvider } from "@/context/UnreadContext";
import { CallProvider } from "@/context/CallContext";
import PaywallProvider from "@/components/paywall/PaywallProvider";
import { MessagingStreamProvider } from "@/context/MessagingStreamContext";
import PushAutoEnablePrompt from "@/components/notifications/PushAutoEnablePrompt";
import NativePushBootstrap from "@/components/notifications/NativePushBootstrap";
import CallScreen from "@/components/calls/CallScreen";
import IncomingCallSheet from "@/components/calls/IncomingCallSheet";
import AdminRoute from "@/components/admin/AdminRoute";

/* -- LAZY (split into separate JS chunks, fetched on first visit) --
   Only the rarely-visited / feature-specific pages remain code-split.
   Each `lazy(() => import(...))` becomes its own webpack chunk so visitors
   who never open these pages don't pay for them. */
const CaseAnalyze         = lazy(() => import("@/pages/CaseAnalyze"));
const DocumentStudio      = lazy(() => import("@/pages/DocumentStudio"));
const ResearchChat        = lazy(() => import("@/pages/ResearchChat"));
const LawsExplorer        = lazy(() => import("@/pages/LawsExplorer"));
const InternalRegulationsExplorer = lazy(() => import("@/pages/InternalRegulationsExplorer"));
const CourtsExplorer      = lazy(() => import("@/pages/CourtsExplorer"));
const Timeline            = lazy(() => import("@/pages/Timeline"));
const Clients             = lazy(() => import("@/pages/Clients"));
const CassationLibrary    = lazy(() => import("@/pages/CassationLibrary"));
const Settings            = lazy(() => import("@/pages/Settings"));
const About               = lazy(() => import("@/pages/About"));
const Help                = lazy(() => import("@/pages/Help"));
const AccountAgreement    = lazy(() => import("@/pages/AccountAgreement"));
const AddStagePage        = lazy(() => import("@/pages/AddStagePage"));
const MyStats             = lazy(() => import("@/pages/MyStats"));
const MySubscription      = lazy(() => import("@/pages/MySubscription"));
const Pricing             = lazy(() => import("@/pages/Pricing"));
const UserGuide           = lazy(() => import("@/pages/UserGuide"));
const LegalPublic         = lazy(() => import("@/pages/LegalPublic"));
const Achievements        = lazy(() => import("@/pages/Achievements"));
const Leaderboard         = lazy(() => import("@/pages/Leaderboard"));
const ClientPortal        = lazy(() => import("@/pages/ClientPortal"));
const PortalShortRedirect = lazy(() => import("@/pages/PortalShortRedirect"));

// Admin pages — almost no end-users hit these; perfect for code-splitting.
const AdminLogin              = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard          = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminFinancials         = lazy(() => import("@/pages/admin/AdminFinancials"));
const AdminCassation          = lazy(() => import("@/pages/admin/AdminCassation"));
const AdminCassationBulk      = lazy(() => import("@/pages/admin/AdminCassationBulk"));
const AdminLawsScraper        = lazy(() => import("@/pages/admin/AdminLawsScraper"));
const AdminInternalRegulationsScraper = lazy(() => import("@/pages/admin/AdminInternalRegulationsScraper"));
const AdminLawArticles        = lazy(() => import("@/pages/admin/AdminLawArticles"));
const AdminRegulationArticles = lazy(() => import("@/pages/admin/AdminRegulationArticles"));
const AdminSjcSync            = lazy(() => import("@/pages/admin/AdminSjcSync"));
const AdminUsers              = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminLawyers            = lazy(() => import("@/pages/admin/AdminLawyers"));
const AdminMonitoring         = lazy(() => import("@/pages/admin/AdminMonitoring"));
const AdminHealthCenter       = lazy(() => import("@/pages/admin/AdminHealthCenter"));
const AdminBackups            = lazy(() => import("@/pages/admin/AdminBackups"));
const AdminChatArchive        = lazy(() => import("@/pages/admin/AdminChatArchive"));
const AdminBarCardDuplicates  = lazy(() => import("@/pages/admin/AdminBarCardDuplicates"));
const AdminBroadcasts         = lazy(() => import("@/pages/admin/AdminBroadcasts"));

/* Empty Suspense fallback — by the time a chunk is fetched, the user has
   already passed the SplashGate, so we don't want a competing spinner;
   a transient empty pane is the cleanest perceived state. */
const RouteFallback = () => (
  <div className="fixed inset-0 bg-[#FAFAFA]" aria-hidden="true" />
);

function App() {
  // Boot the Capacitor-native shell (status bar, deep links, splash hide).
  // No-op on the web/PWA, so safe to always call.
  useEffect(() => { initNativeShell(); }, []);

  return (
    <div className="App" dir="rtl">
      <BrowserRouter>
      <ScrollToTop />
      <ErrorBoundary>
        <UserAuthProvider>
        <AIConsentProvider>
        <SplashGate>
          <AdminAuthProvider>
            <MessagingStreamProvider>
            <UnreadProvider>
            <CallProvider>
            <PaywallProvider>
            <PushAutoEnablePrompt />
            <NativePushBootstrap />
            <ProductTour />
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public landing + auth — eager */}
              <Route path="/" element={<Layout><Landing /></Layout>} />
              <Route path="/login"  element={<AuthHub mode="login"  />} />
              {/* صَفحَة عامَّة (بِدون UserRoute) لِلشُّروط وَالخُصوصيّة — مَطلوبَة
                  لِلـApp Store/Play Store + لِلوُصول مِن صَفحَة تَسجيل الدُّخول. */}
              <Route path="/legal"  element={<LegalPublic />} />
              <Route path="/signup" element={<AuthHub mode="signup" />} />

              {/* Marketing / informational */}
              <Route path="/about"   element={<Layout><About /></Layout>} />
              <Route path="/help"    element={<Layout><Help /></Layout>} />

              {/* Lawyer-area routes — gated by UserRoute */}
              <Route path="/cases"                element={<UserRoute><Layout><CaseDashboard /></Layout></UserRoute>} />
              <Route path="/cases/new"            element={<UserRoute><Layout><CaseAnalyze /></Layout></UserRoute>} />
              {/* Add/Edit Stage — full pages instead of modals because iOS
                  Capacitor WKWebView breaks scroll inside complex dialogs. */}
              <Route path="/cases/:caseId/add-stage"               element={<UserRoute><AddStagePage /></UserRoute>} />
              <Route path="/cases/:caseId/stages/:stageId/edit"    element={<UserRoute><AddStagePage /></UserRoute>} />
              {/* Legacy alias — many older links still point to /analyze.
                  Routing both paths to CaseAnalyze prevents white-screen
                  navigations until every reference is migrated. */}
              <Route path="/analyze"              element={<UserRoute><Layout><CaseAnalyze /></Layout></UserRoute>} />
              <Route path="/draft"                element={<UserRoute><Layout><DocumentStudio /></Layout></UserRoute>} />
              <Route path="/chat"                 element={<UserRoute><Layout><ResearchChat /></Layout></UserRoute>} />
              <Route path="/research"             element={<UserRoute><Layout><ResearchChat /></Layout></UserRoute>} />
              <Route path="/laws"                 element={<UserRoute><Layout><LawsExplorer /></Layout></UserRoute>} />
              <Route path="/internal-regulations" element={<UserRoute><Layout><InternalRegulationsExplorer /></Layout></UserRoute>} />
              <Route path="/courts"               element={<UserRoute><Layout><CourtsExplorer /></Layout></UserRoute>} />
              <Route path="/timeline"             element={<UserRoute><Layout><Timeline /></Layout></UserRoute>} />
              <Route path="/clients"              element={<UserRoute><Layout><Clients /></Layout></UserRoute>} />
              <Route path="/cassation"            element={<UserRoute><Layout><CassationLibrary /></Layout></UserRoute>} />
              <Route path="/settings"             element={<UserRoute><Layout><Settings /></Layout></UserRoute>} />
              <Route path="/account"              element={<UserRoute><Layout><Account /></Layout></UserRoute>} />
              <Route path="/account/presence"     element={<UserRoute><AccountPresence /></UserRoute>} />
              <Route path="/account/agreement"    element={<UserRoute><AccountAgreement /></UserRoute>} />
              <Route path="/account/stats"        element={<UserRoute><MyStats /></UserRoute>} />
              <Route path="/account/subscription" element={<UserRoute><MySubscription /></UserRoute>} />
              <Route path="/pricing"              element={<Pricing />} />
              <Route path="/help/guide"           element={<UserRoute><UserGuide /></UserRoute>} />
              <Route path="/achievements"         element={<UserRoute><Layout><Achievements /></Layout></UserRoute>} />
              <Route path="/leaderboard"          element={<UserRoute><Layout><Leaderboard /></Layout></UserRoute>} />
              <Route path="/lawyers"              element={<Layout><LawyersDirectory /></Layout>} />
              <Route path="/lawyers/:slug"        element={<Layout><LawyerProfile /></Layout>} />
              <Route path="/notifications"        element={<UserRoute><Layout><NotificationsPage /></Layout></UserRoute>} />
              <Route path="/messages"             element={<UserRoute><Layout><MessagesList /></Layout></UserRoute>} />
              <Route path="/messages/:id"         element={<UserRoute><MessageRoom /></UserRoute>} />

              {/* Client portal — magic link based, no auth */}
              <Route path="/portal/:token" element={<ClientPortal />} />
              <Route path="/p/:short"      element={<PortalShortRedirect />} />

              {/* Admin area — separate auth */}
              <Route path="/admin/login"               element={<AdminLogin />} />
              <Route path="/admin"                     element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/financials"          element={<AdminRoute><AdminFinancials /></AdminRoute>} />
              <Route path="/admin/cassation"           element={<AdminRoute><AdminCassation /></AdminRoute>} />
              <Route path="/admin/cassation-bulk"      element={<AdminRoute><AdminCassationBulk /></AdminRoute>} />
              <Route path="/admin/laws-scraper"        element={<AdminRoute><AdminLawsScraper /></AdminRoute>} />
              <Route path="/admin/regulations-scraper" element={<AdminRoute><AdminInternalRegulationsScraper /></AdminRoute>} />
              <Route path="/admin/laws/:lawId/articles" element={<AdminRoute><AdminLawArticles /></AdminRoute>} />
              <Route path="/admin/regulations/:regulationId/articles" element={<AdminRoute><AdminRegulationArticles /></AdminRoute>} />
              <Route path="/admin/sjc-sync"            element={<AdminRoute><AdminSjcSync /></AdminRoute>} />
              <Route path="/admin/users"               element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/lawyers"             element={<AdminRoute><AdminLawyers /></AdminRoute>} />
              <Route path="/admin/monitoring"          element={<AdminRoute><AdminMonitoring /></AdminRoute>} />
              <Route path="/admin/health-center"       element={<AdminRoute><AdminHealthCenter /></AdminRoute>} />
              <Route path="/admin/backups"             element={<AdminRoute><AdminBackups /></AdminRoute>} />
              <Route path="/admin/chat-archive"        element={<AdminRoute><AdminChatArchive /></AdminRoute>} />
              <Route path="/admin/bar-card-duplicates" element={<AdminRoute><AdminBarCardDuplicates /></AdminRoute>} />
              <Route path="/admin/broadcasts"          element={<AdminRoute><AdminBroadcasts /></AdminRoute>} />
            </Routes>
            </Suspense>
            {/* Global call overlays — render above every route */}
            <CallScreen />
            <IncomingCallSheet />
            </PaywallProvider>
            </CallProvider>
            </UnreadProvider>
            </MessagingStreamProvider>
          </AdminAuthProvider>
        </SplashGate>
        </AIConsentProvider>
        </UserAuthProvider>
      </ErrorBoundary>
        <Toaster
          position="top-center"
          dir="rtl"
          /* offset = ارتفاع منطقة الـ Notch / Status Bar في iOS (الـ "dead zone")
             + 14px إضافيّة لِيَظهَر الـ toast واضِحاً تَحت الشَّريط مُباشَرة.
             ⚠️ Sonner لديه مَسار مُنفَصِل لِلموبايل (< 600px) يَستَخدِم
             `--mobile-offset-top` بدل `--offset-top` — لذا يَجب ضَبط
             الاثنَين معاً، وإلّا تَقَع رَسائل الموبايل في المنطقة المَيِّتة
             تَحت الـ Notch. */
          offset="calc(env(safe-area-inset-top, 0px) + 14px)"
          mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 14px)", left: "12px", right: "12px" }}
          /* تَأكيد فَوقيّة الـ toast على أيّ شَيء حَتى الـ headers الزُّجاجيّة. */
          style={{ zIndex: 99999 }}
          toastOptions={{
            duration: 4500,
            style: {
              fontFamily: "'Cairo', sans-serif",
              borderRadius: "16px",
              border: "1px solid rgba(212,175,55,0.35)",
              boxShadow: "0 10px 30px rgba(10,25,47,0.18)",
              padding: "14px 16px",
              fontSize: "14.5px",
              lineHeight: "1.7",
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}

export default App;
